import React from 'react';
import { Form } from 'react-bootstrap';
import { Library } from 'lucide-react';
import { MaintenanceIntervalType, CostUnit } from '../types/Enums';
import { MaintenanceLibraryFields } from '../types/LibraryMaintenanceTemplate';
import { LibraryFormData } from '../types/LibraryTemplate';
import { formatCostUnit } from '../Helpers/maintenanceCostUtils';
import {
    parseCsvCost,
    formatCsvCost,
    parseCsvCostUnit,
    formatCsvCostUnit,
    CSV_COST_UNIT_TOKENS,
} from '../Helpers/csvUtils';
import { LibraryConfig, COST_UNIT_OPTIONS, isCostUnit } from './LibraryConfig';
import NumberInput from '../Components/NumberInput';

const INTERVAL_TYPE_OPTIONS: ReadonlyArray<{ value: MaintenanceIntervalType; label: string }> = [
    { value: MaintenanceIntervalType.days, label: 'Dagar' },
    { value: MaintenanceIntervalType.weeks, label: 'Veckor' },
    { value: MaintenanceIntervalType.months, label: 'Månader' },
    { value: MaintenanceIntervalType.years, label: 'År' },
];

/** CSV spelling of each interval type, both directions. */
const INTERVAL_TYPE_FROM_CSV: ReadonlyMap<string, MaintenanceIntervalType> = new Map([
    ['år', MaintenanceIntervalType.years],
    ['månader', MaintenanceIntervalType.months],
    ['veckor', MaintenanceIntervalType.weeks],
    ['dagar', MaintenanceIntervalType.days],
]);
const INTERVAL_TYPE_TO_CSV: Record<MaintenanceIntervalType, string> = {
    [MaintenanceIntervalType.years]: 'år',
    [MaintenanceIntervalType.months]: 'månader',
    [MaintenanceIntervalType.weeks]: 'veckor',
    [MaintenanceIntervalType.days]: 'dagar',
};

const PICKER_INTERVAL_LABELS: Record<MaintenanceIntervalType, string> = {
    [MaintenanceIntervalType.years]: 'år',
    [MaintenanceIntervalType.months]: 'mån',
    [MaintenanceIntervalType.weeks]: 'v',
    [MaintenanceIntervalType.days]: 'd',
};

/** Accepts exactly "<n> år|månader|veckor|dagar" (anchored, so "30.5 år" is rejected). */
function parseCsvInterval(interval: string): { value: number; type: MaintenanceIntervalType } | null {
    const match = interval.match(/^\s*(\d+)\s*(år|månader|veckor|dagar)\s*$/i);
    if (!match) return null;
    const type = INTERVAL_TYPE_FROM_CSV.get(match[2].toLowerCase());
    const value = parseInt(match[1], 10);
    if (!type || value <= 0) return null;
    return { value, type };
}

type MaintenanceForm = LibraryFormData<MaintenanceLibraryFields>;

const MaintenanceFields: React.FC<{
    form: MaintenanceForm;
    setForm: React.Dispatch<React.SetStateAction<MaintenanceForm>>;
    disabled: boolean;
}> = ({ form, setForm, disabled }) => (
    <>
        <div className="row g-3">
            <div className="col-md-4">
                <Form.Group>
                    <Form.Label>Intervall</Form.Label>
                    <NumberInput
                        className="form-control"
                        min="1"
                        step="1"
                        integer
                        emptyValue={1}
                        value={form.interval_value}
                        onChange={(value) => setForm((prev) => ({ ...prev, interval_value: value }))}
                        disabled={disabled}
                    />
                </Form.Group>
            </div>
            <div className="col-md-4">
                <Form.Group>
                    <Form.Label>Enhet</Form.Label>
                    <Form.Select
                        value={form.interval_type}
                        onChange={(e) =>
                            setForm((prev) => ({ ...prev, interval_type: e.target.value as MaintenanceIntervalType }))
                        }
                        disabled={disabled}
                    >
                        {INTERVAL_TYPE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>
            </div>
            <div className="col-md-4">
                <Form.Group>
                    <Form.Label>Kostnadsenhet</Form.Label>
                    <Form.Select
                        value={form.cost_unit}
                        onChange={(e) => setForm((prev) => ({ ...prev, cost_unit: e.target.value as CostUnit }))}
                        disabled={disabled}
                    >
                        {COST_UNIT_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>
            </div>
        </div>

        <Form.Group className="mt-3">
            <Form.Label>Kostnad ({formatCostUnit(form.cost_unit)})</Form.Label>
            <NumberInput
                className="form-control"
                min="0"
                step="1"
                integer
                value={form.cost}
                onChange={(value) => setForm((prev) => ({ ...prev, cost: value }))}
                disabled={disabled}
            />
        </Form.Group>
    </>
);

/**
 * Maintenance activity library: interval + cost templates that admins copy
 * when creating maintenance templates for a component type.
 */
export const maintenanceLibrary: LibraryConfig<MaintenanceLibraryFields> = {
    path: 'config/maintenanceLibraryTemplates',
    fieldKeys: ['interval_type', 'interval_value', 'cost', 'cost_unit'],
    defaultFields: {
        interval_type: MaintenanceIntervalType.years,
        interval_value: 1,
        cost: 0,
        cost_unit: 'per_sqm',
    },

    parseFields(d, key) {
        if (typeof d.interval_type !== 'string') {
            console.warn(`Invalid library template ${key}: missing interval_type`);
            return null;
        }
        if (typeof d.interval_value !== 'number') {
            console.warn(`Invalid library template ${key}: missing interval_value`);
            return null;
        }
        if (typeof d.cost !== 'number') {
            console.warn(`Invalid library template ${key}: missing cost`);
            return null;
        }
        if (typeof d.cost_unit !== 'string') {
            console.warn(`Invalid library template ${key}: missing cost_unit`);
            return null;
        }
        return {
            interval_type: d.interval_type as MaintenanceIntervalType,
            interval_value: d.interval_value,
            cost: d.cost,
            cost_unit: d.cost_unit as CostUnit,
        };
    },

    validateFields(f, mode) {
        const check = <K extends keyof MaintenanceLibraryFields>(key: K) => mode === 'full' || f[key] !== undefined;

        if (check('interval_type') && !f.interval_type) {
            throw new Error('Intervalltyp saknas');
        }
        if (check('interval_value') && (f.interval_value === undefined || f.interval_value <= 0)) {
            throw new Error('Intervallvärde måste vara större än 0');
        }
        if (check('cost') && (f.cost === undefined || !Number.isInteger(f.cost) || f.cost < 0)) {
            throw new Error('Kostnad måste vara ett heltal, 0 eller större');
        }
        if (check('cost_unit') && !isCostUnit(f.cost_unit)) {
            throw new Error('Ogiltig kostnadsenhet');
        }
    },

    labels: {
        title: 'Underhållsbibliotek',
        description: 'Hantera mallar som användare kan kopiera när de skapar underhållsaktiviteter',
        icon: Library,
        loadError: 'Kunde inte ladda mallbiblioteket',
        pickerTitle: 'Välj mall från bibliotek',
        addNamePlaceholder: 'T.ex. Byta takplåt',
        deleteNote: 'Redan kopierade underhållsaktiviteter påverkas inte.',
        resetNoun: 'underhållsbiblioteket',
        csvHelp:
            'CSV-filen ska ha kolumnerna: Namn, Pris (heltal i kr, t.ex. "2500 kr"), Enhet (m2/st/m), ' +
            'Intervall (t.ex. "30 år"), Bastyper (kommaseparerade). Filer med fel i någon rad importeras inte.',
        exportFilePrefix: 'underhallsbibliotek',
    },

    csv: {
        columns: ['Pris', 'Enhet', 'Intervall'],
        parseColumns([priceStr, unit, interval]) {
            const cost = parseCsvCost(priceStr);
            if (cost === null) {
                return {
                    ok: false,
                    label: 'Ogiltig kostnad',
                    detail: `Ogiltig kostnad: "${priceStr}" (endast heltal i kr, t.ex. "2500 kr")`,
                };
            }
            const cost_unit = parseCsvCostUnit(unit);
            if (!cost_unit) {
                return {
                    ok: false,
                    label: 'Okänd enhet',
                    detail: `Okänd enhet: "${unit}" (endast ${CSV_COST_UNIT_TOKENS} stöds)`,
                };
            }
            const parsedInterval = parseCsvInterval(interval);
            if (!parsedInterval) {
                return {
                    ok: false,
                    label: 'Ogiltigt intervall',
                    detail: interval ? `Ogiltigt intervall: "${interval}" (t.ex. "30 år")` : 'Tomt intervall',
                };
            }
            return {
                ok: true,
                fields: {
                    interval_type: parsedInterval.type,
                    interval_value: parsedInterval.value,
                    cost,
                    cost_unit,
                },
            };
        },
        formatColumns(t) {
            return [
                formatCsvCost(t.cost),
                formatCsvCostUnit(t.cost_unit),
                `${t.interval_value} ${INTERVAL_TYPE_TO_CSV[t.interval_type] || ''}`,
            ];
        },
    },

    renderFields(form, setForm, disabled) {
        return <MaintenanceFields form={form} setForm={setForm} disabled={disabled} />;
    },

    formatSummary(t) {
        const interval = `${t.interval_value} ${PICKER_INTERVAL_LABELS[t.interval_type] || t.interval_type}`;
        return `${interval} · ${t.cost.toLocaleString('sv-SE')} ${formatCostUnit(t.cost_unit)}`;
    },
};
